import {
	BatteryFull,
	Buildings,
	CellSignalFull,
	CheckCircle,
	ClockCountdown,
	User,
	WifiSlash,
	XCircle,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import type {
	ReadingAdvantSource,
	ReadingDetail,
	ReadingMqttSource,
} from "@/api/reseller/types";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoCard, InfoCardSkeleton } from "./reading-detail-info-card";

interface ReadingDetailInfoCardsProps {
	data: ReadingDetail | undefined;
	isLoading: boolean;
}

export function ReadingDetailInfoCards({
	data,
	isLoading,
}: ReadingDetailInfoCardsProps) {
	const { t, i18n } = useTranslation();

	if (isLoading) {
		return (
			<div className="flex flex-col gap-3">
				<GatewayCardSkeleton />
				<div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
					<InfoCardSkeleton />
					<InfoCardSkeleton />
					<InfoCardSkeleton />
					<InfoCardSkeleton />
					<InfoCardSkeleton />
				</div>
			</div>
		);
	}

	if (!data) return null;

	const source = data.mqtt_source ?? data.advant_source ?? null;

	return (
		<div className="flex flex-col gap-3">
			<GatewayCard source={source} locale={i18n.language} />
			<div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
				<InfoCard
					icon={<User className="size-4" />}
					label={t("readingDetail.requestedBy")}
					value={
						data.requested_by ? (
							<span className="text-xs">{data.requested_by.email}</span>
						) : (
							t("readingDetail.automatic")
						)
					}
				/>
				<InfoCard
					icon={<CheckCircle className="size-4" />}
					label={t("readingDetail.completed")}
					value={data.cnt_completed}
				/>
				<InfoCard
					icon={<ClockCountdown className="size-4" />}
					label={t("readingDetail.requestSent")}
					value={data.cnt_request_sent}
				/>
				<InfoCard
					icon={<XCircle className="size-4" />}
					label={t("readingDetail.errors")}
					value={data.cnt_error}
				/>
				<InfoCard
					icon={<WifiSlash className="size-4" />}
					label={t("readingDetail.noCommunication")}
					value={data.cnt_no_communication}
				/>
			</div>
		</div>
	);
}

interface GatewayCardProps {
	source: ReadingMqttSource | ReadingAdvantSource | null;
	locale: string;
}

function GatewayCard({ source, locale }: GatewayCardProps) {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col gap-3 rounded-lg border bg-card px-4 py-3">
			<div className="flex items-center gap-3">
				<div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
					<CellSignalFull className="size-4" />
				</div>
				<div className="flex min-w-0 flex-col">
					<span className="text-xs text-muted-foreground">
						{t("readingDetail.gateway")}
					</span>
					<span className="truncate font-mono text-sm font-medium">
						{source?.sn ?? "—"}
					</span>
				</div>
			</div>
			<div className="grid grid-cols-1 gap-x-4 gap-y-2 border-t pt-3 min-[480px]:grid-cols-3">
				<GatewayField
					icon={<Buildings className="size-3.5" />}
					label={t("readingDetail.gatewayDistrict")}
					value={
						source?.district_name ?? (
							<span className="text-muted-foreground">
								{t("readingDetail.gatewayNoDistrict")}
							</span>
						)
					}
				/>
				<GatewayField
					icon={<CellSignalFull className="size-3.5" />}
					label={t("readingDetail.gatewayLastCommunication")}
					value={
						source?.last_communication ? (
							new Date(source.last_communication).toLocaleString(locale, {
								dateStyle: "short",
								timeStyle: "short",
							})
						) : (
							<span className="text-muted-foreground">
								{t("readingDetail.gatewayNoCommunication")}
							</span>
						)
					}
				/>
				<GatewayField
					icon={<BatteryFull className="size-3.5" />}
					label={t("readingDetail.gatewayBattery")}
					value={
						source && "battery_level" in source && source.battery_level != null
							? `${source.battery_level}%`
							: "—"
					}
				/>
			</div>
		</div>
	);
}

interface GatewayFieldProps {
	icon: React.ReactNode;
	label: string;
	value: React.ReactNode;
}

function GatewayField({ icon, label, value }: GatewayFieldProps) {
	return (
		<div className="flex min-w-0 flex-col gap-0.5">
			<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
				{icon}
				{label}
			</span>
			<span className="truncate text-sm font-medium">{value}</span>
		</div>
	);
}

function GatewayCardSkeleton() {
	return (
		<div className="flex flex-col gap-3 rounded-lg border bg-card px-4 py-3">
			<div className="flex items-center gap-3">
				<Skeleton className="size-9 rounded-md" />
				<div className="flex flex-col gap-1">
					<Skeleton className="h-3 w-16" />
					<Skeleton className="h-4 w-32" />
				</div>
			</div>
			<div className="grid grid-cols-1 gap-3 border-t pt-3 min-[480px]:grid-cols-3">
				<Skeleton className="h-8" />
				<Skeleton className="h-8" />
				<Skeleton className="h-8" />
			</div>
		</div>
	);
}
